import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { INGREDIENT_OPTIONS, RECIPE_ACTIONS } from '@usf/ngrx-tools';
import { RecipeViewModelSelectors } from '../../recipes/selectors/recipe-view-model-selectors';
import {
  firstValueFrom,
  Observable,
  BehaviorSubject,
  of,
  map,
  tap,
  combineLatest,
  switchMap,
} from 'rxjs';
import { recipeDetailViewModel } from '@app/mpp/model/recipe-detail-view-model';
import { TranslateService } from '@ngx-translate/core';
import { Button } from '@shared/models/ellipsis-option.model';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { PlatformService } from '@panamax/app-state';
import { UOM, getUOMTranslation } from '@app/mpp/shared/enum/mpp-enums';
import { IngredientCard } from '@app/mpp/model/ingredient-card-model';
import { IngredientSearchResult } from '@app/mpp/shared/model/ingredient-search-result';
import { RecipeProductsTypeaheadService } from '@shared/services/typeahead/recipe-products-typeahead.service';
import { ProductService } from '@shared/services/product/product.service';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { ProductPriceUtil } from '@product-detail/utils/product-price.util';
import { selectProductDetailState } from '@usf/ngrx-product';
import { selectedCustomer } from 'src/app/ngrx-customer/store/selectors/customer.selectors';

@Injectable({
  providedIn: 'root',
})
export class RecipeDetailService {
  private recipeDetailViewModel$ = this.store.select(
    this.recipeViewModelSelectors.recipeDetailViewModel$,
  );
  private groupModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  groupModalIsOpen$: Observable<boolean> =
    this.groupModalIsOpenSubject.asObservable();

  private editDetailsModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  editDetailsModalIsOpen$: Observable<boolean> =
    this.editDetailsModalIsOpenSubject.asObservable();

  private ingredientSearchModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  ingredientSearchModalIsOpen$: Observable<boolean> =
    this.ingredientSearchModalIsOpenSubject.asObservable();

  private searchResultsForIngredient$ = this.store.select(
    this.recipeViewModelSelectors.getSearchResultsForIngredient$,
  );

  private searchTimeoutTracker = new BehaviorSubject<boolean>(false);
  searchTimeoutTracker$: Observable<boolean> =
    this.searchTimeoutTracker.asObservable();
  private typeAheadResults$ = this.typeAheadService.products$.pipe(
    tap(products => {
      this.productService.loadProducts(
        products.map(value => Number(value.ec_product_id)),
      );
      this.productService.loadProductDetails(
        products.map(value => Number(value.ec_product_id)),
      );
    }),
    switchMap(products =>
      combineLatest([
        this.store.select(productWithAlternativesSelector),
        this.store.select(selectedCustomer),
        this.store.select(selectProductDetailState),
        of(products),
      ]).pipe(
        map(([productMap, selectedCustomer, details, products]) => {
          return products.map(value => {
            const productDetail = details.entities[Number(value.ec_product_id)];
            const product = productMap.get(Number(value.ec_product_id));
            const pricePerPortion = ProductPriceUtil.calculateCostBreakdown(
              product?.summary,
              selectedCustomer,
              product?.pricing,
              productDetail?.standardComparisonUOM,
              productDetail?.standardComparisonValue,
            );
            console.log('--------------------------------');
            console.log(product?.productNumber);
            console.log(pricePerPortion);
            console.log(product?.productTags);
            console.log(productDetail);
            console.log('--------------------------------');
            return {
              id: value.ec_product_id,
              name: value.productName,
              types: INGREDIENT_OPTIONS.PRODUCT,
              typeAhaedData: value,
              product,
              brand: product?.summary?.brand,
              price: product?.pricing?.unitPrice,
              priceUOM: product?.pricing?.priceUom,
              pricePerPortion: pricePerPortion,
              salesPackSize: product?.summary?.salesPackSize,
              pills: product?.productTags,
            } as IngredientSearchResult;
          });
        }),
      ),
    ),
  );

  public get typeAheadResults(): Observable<IngredientSearchResult[]> {
    return this.typeAheadResults$;
  }

  public get searchResultsForIngredient(): Observable<
    IngredientSearchResult[]
  > {
    return this.searchResultsForIngredient$;
  }

  lastCardData: IngredientCard;

  private profileImageModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  profileImageModalIsOpen$: Observable<boolean> =
    this.profileImageModalIsOpenSubject.asObservable();

  searchTimeout: any;

  constructor(
    private store: Store,
    readonly recipeViewModelSelectors: RecipeViewModelSelectors,
    readonly translateService: TranslateService,
    readonly popoverController: PopoverController,
    readonly actionSheetController: ActionSheetController,
    readonly platformService: PlatformService,
    readonly typeAheadService: RecipeProductsTypeaheadService,
    readonly productService: ProductService,
  ) {}

  public get recipeViewModel(): Observable<recipeDetailViewModel> {
    return this.recipeDetailViewModel$;
  }

  public updateSelectedRecipe(recipeId: string) {
    this.store.dispatch(RECIPE_ACTIONS.selectRecipeForPageView({ recipeId }));
  }

  public updateShowHidePortion(show: boolean) {
    this.store.dispatch(RECIPE_ACTIONS.showHidePortion({ show }));
  }

  public showServingUOMDropdown(event: Event, recipeID: string) {
    const buttons = this.getUOMButtons(this.updateServingUOM, recipeID);
    this.platformService.isTouch.value
      ? this.openSelectionsTouch(buttons)
      : this.openSelectionsDesktop(event, buttons);
  }
  public async showGroupNameDropdown(event: Event, recipeID: string) {
    const groups = await firstValueFrom(
      this.store.select(this.recipeViewModelSelectors.getGroupsForRecipes$),
    );
    const buttons = this.getGroupButtons(groups, recipeID);
    this.platformService.isTouch.value
      ? this.openSelectionsTouch(buttons)
      : this.openSelectionsDesktop(event, buttons);
  }

  public showYieldUOMDropdown(event: Event, recipeID: string) {
    const buttons = this.getUOMButtons(this.updateYieldUOM, recipeID);
    this.platformService.isTouch.value
      ? this.openSelectionsTouch(buttons)
      : this.openSelectionsDesktop(event, buttons);
  }

  public updateYield(value: number, recipeID: string) {
    const num = new Number(value).valueOf();
    this.store.dispatch(
      RECIPE_ACTIONS.updateYieldValue({ recipeId: recipeID, value: num }),
    );
  }

  public updateServing(value: number, recipeID: string) {
    const num = new Number(value).valueOf();
    this.store.dispatch(
      RECIPE_ACTIONS.updateServingValue({ recipeId: recipeID, value: num }),
    );
  }

  public updateServingPerRecipe(value: number, recipeID: string) {
    const num = new Number(value).valueOf();
    this.store.dispatch(
      RECIPE_ACTIONS.updateServingPerRecipeValue({
        recipeId: recipeID,
        value: num,
      }),
    );
  }

  public updateServingUOM = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateServingUOM({ recipeId: recipeID, value }),
    );
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  public updateYieldUOM = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateYieldUOM({ recipeId: recipeID, value }),
    );
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  public updateGroupName = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateGroupName({ recipeId: recipeID, groupName: value }),
    );
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  public updateRecipeName = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateRecipeName({ recipeId: recipeID, name: value }),
    );
  };

  public updateDescription = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateDescription({
        recipeId: recipeID,
        description: value,
      }),
    );
  };

  async openSelectionsDesktop(event: Event, buttons: Button[]) {
    const popover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      componentProps: {
        buttons,
      },
      showBackdrop: false,
      cssClass: 'ellipsis-options-popover-desktop',
      event,
      mode: 'ios',
      alignment: 'center',
    });
    await popover.present();
  }

  async openSelectionsTouch(buttons: Button[]) {
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  public openGroupNameModal() {
    this.groupModalIsOpenSubject.next(true);
  }

  public handleGroupNameModalDismiss() {
    this.groupModalIsOpenSubject.next(false);
  }

  public handleCustomGroupSubmit(groupName: string, recipeID: string) {
    this.updateGroupName(groupName, recipeID);
    this.groupModalIsOpenSubject.next(false);
  }

  public getGroupButtons(groups: string[], recipeID: string) {
    const buttons = groups.map(group => {
      return {
        text: group,
        handler: () => this.updateGroupName(group, recipeID),
      };
    });

    buttons.push({
      text: 'Add Custom',
      handler: () => this.openGroupNameModal(),
    });

    return buttons;
  }

  public getUOMButtons(callback: Function, recipeID: string) {
    return [
      {
        text: this.translateService.instant(getUOMTranslation(UOM.EACH)),
        handler: () => callback(UOM.EACH, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.POUND)),
        handler: () => callback(UOM.POUND, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.OUNCE)),
        handler: () => callback(UOM.OUNCE, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.GRAM)),
        handler: () => callback(UOM.GRAM, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.GALLON)),
        handler: () => callback(UOM.GALLON, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.QUART)),
        handler: () => callback(UOM.QUART, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.PINT)),
        handler: () => callback(UOM.PINT, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.CUP)),
        handler: () => callback(UOM.CUP, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.FLUID_OUNCE)),
        handler: () => callback(UOM.FLUID_OUNCE, recipeID),
      },
    ];
  }

  public getMobileActionsButtons(recipeID: string): Button[] {
    const buttons: Button[] = [];

    if (
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile
    ) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.menuProfitPro.actions.editDetails',
        ),
        handler: () => this.openEditDetailsModal(),
      });
    }

    return buttons;
  }

  public openEditDetailsModal(): void {
    this.editDetailsModalIsOpenSubject.next(true);
  }

  public dismissEditDetailsModal(): void {
    this.editDetailsModalIsOpenSubject.next(false);
  }

  public handleEditDetailsSubmission(
    updates: Map<string, string>,
    recipeID: string,
  ): void {
    if (updates.has('description')) {
      this.updateDescription(updates.get('description') || '', recipeID);
    }
    if (updates.has('name')) {
      this.updateRecipeName(updates.get('name') || '', recipeID);
    }
    if (updates.has('group')) {
      this.updateGroupName(updates.get('group') || '', recipeID);
    }
    this.dismissEditDetailsModal();
  }

  public openProfileImageModal(): void {
    this.profileImageModalIsOpenSubject.next(true);
  }

  public dismissProfileImageModal(): void {
    this.profileImageModalIsOpenSubject.next(false);
  }

  public handleProfileImageModalOnSave(
    updates: Map<string, string>,
    recipeID: string,
  ): void {
    const fileName = updates.get('fileName') ?? '';
    const imageType = updates.get('extension') ?? '';
    const base64Value = updates.get('value') ?? '';

    this.store.dispatch(
      RECIPE_ACTIONS.uploadRecipeProfileImageToS3({
        uploadImage: {
          recipeId: recipeID,
          profileImage: {
            name: fileName,
            type: imageType,
            value: base64Value,
          },
        },
      }),
    );
    this.dismissProfileImageModal();
  }

  public async showMobileActions(recipeID: string): Promise<void> {
    const buttons = this.getMobileActionsButtons(recipeID);

    if (buttons.length === 0) {
      return;
    }

    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  public openIngredientSearchModal(): void {
    this.typeAheadService.initializeRecipeProductsTypeAhead();
    this.ingredientSearchModalIsOpenSubject.next(true);
    this.store.dispatch(
      RECIPE_ACTIONS.updateIngredientSearchKey({ searchKey: '' }),
    );
    this.typeAheadService.handleInputChange('');
  }

  public closeIngredientSearchModal(): void {
    this.ingredientSearchModalIsOpenSubject.next(false);
    this.typeAheadService.disableTypeahead();
  }

  public handleNewIngredientSearchRequest(value: string) {
    this.searchTimeoutTracker.next(true);
    // wanting to avoid the searching showing no results while typing
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.searchTimeoutTracker.next(false);
    }, 750);
    this.typeAheadService.handleInputChange(value);
    this.store.dispatch(
      RECIPE_ACTIONS.updateIngredientSearchKey({ searchKey: value }),
    );
  }
}
